import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { BulkEditSkusInput } from '@threadheads/shared';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useRole } from '@/hooks/use-role';
import { CsvImportWizard } from '@/components/csv-import-wizard/CsvImportWizard';
import { NoteLine } from '@/components/shared/NoteLine';
import ErrorTable, { type ErrorTableColumn } from './bulk-stock-upload/ErrorTable';
import { parseSkuBulkEditCsv, type SkuBulkEditCsvError } from '../utils/skuBulkEditCsv';
import { translateCsvParseError } from '../utils/csvErrorMessages';
import { formatCsvParseError } from '../utils/csvParse';

/**
 * Products > Bulk edit CSV: the Default cost, size and color of any SKU in
 * the caller's own catalog, from the Products export. A config over the
 * shared CsvImportWizard.
 *
 * The SERVER previews (a dry run: old to new per field, skipped values);
 * Apply re-runs the same request for real, which writes the changes and the
 * Logs > Adjustments row in one transaction. A blank cell leaves a value as
 * it is; clearing is done in the SKU edit modal. A cost carries its own
 * currency (null = the org currency) and shows as "22.50 USD", never
 * converted; size and color are text.
 */

type EditResult = RouterOutputs['inventory']['bulkEditSkus'];
type Change = EditResult['sample'][number];
type Field = keyof EditResult['changed'];
type EditData = { items: BulkEditSkusInput['items']; sourceRows: number[]; filename: string };
type SkippedRow = {
  index: number;
  sku: string;
  field: Field | null;
  value: string;
  reason: 'unknown' | EditResult['skipped'][number]['reason'];
};

const FIELDS: Field[] = ['cost', 'size', 'color'];

const FIELD_KEYS: Record<Field, string> = {
  cost: 'skuBulkEdit.field.cost',
  size: 'skuBulkEdit.field.size',
  color: 'skuBulkEdit.field.color',
};

const REASON_KEYS: Record<SkippedRow['reason'], string> = {
  unknown: 'skuBulkEdit.reason.unknown',
  not_a_number: 'skuBulkEdit.reason.notANumber',
  too_large: 'skuBulkEdit.reason.tooLarge',
  conflicting_duplicate: 'skuBulkEdit.reason.duplicate',
  shopify_owned: 'skuBulkEdit.reason.shopifyOwned',
  unsupported_currency: 'skuBulkEdit.reason.unsupportedCurrency',
  supply_alias: 'skuBulkEdit.reason.supplyAlias',
};

/** A refused file names at most this many rows, then "and N more". */
const LISTED_ROWS = 10;

const totalOf = (changed: EditResult['changed']) => changed.cost + changed.size + changed.color;

interface SkuBulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SkuBulkEditModal: React.FC<SkuBulkEditModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('inventory');
  const { formatWithCode, currency } = useCurrencyFormatter();
  // Default cost needs skus.costs_manage on the server; for anyone without it the
  // file's cost and currency cells are dropped so an exported file still uploads
  // for size and color.
  const { can } = useRole();
  const canWriteCosts = can('skus.costs_manage');
  const utils = trpc.useUtils();
  const mutation = trpc.inventory.bulkEditSkus.useMutation();

  const rowList = (lines: number[]) => {
    const rows = lines.slice(0, LISTED_ROWS).join(', ');
    const more = lines.length - LISTED_ROWS;
    return more > 0 ? t('skuBulkEdit.rowsAndMore', { rows, count: more }) : rows;
  };

  const describeFileError = (error: SkuBulkEditCsvError): string => {
    switch (error.code) {
      case 'cell_too_long':
        return t('skuBulkEdit.cellTooLong', { rows: rowList(error.lines) });
      case 'option_name_mismatch':
        return t('skuBulkEdit.optionName', { rows: rowList(error.lines) });
      case 'no_editable_column':
        return t('skuBulkEdit.noEditableColumn');
      case 'missing_column':
        if (error.column === 'sku') return t('skuBulkEdit.missingSku');
        break;
    }
    return translateCsvParseError(error, t, formatCsvParseError(error));
  };

  const parse = (text: string, file: File) => {
    const parsed = parseSkuBulkEditCsv(text, {
      oldDefaultCostReason: t('skuBulkEdit.oldColumn'),
      styleReason: t('skuBulkEdit.styleColumn'),
    });
    if (parsed.errors.length > 0) return { errors: parsed.errors.map(describeFileError) };
    if (parsed.items.length === 0) return { errors: [t('skuBulkEdit.noRows')] };
    const items = canWriteCosts
      ? parsed.items
      : parsed.items.map(({ cost: _cost, currency: _currency, ...rest }) => rest);
    return { data: { items, sourceRows: parsed.sourceRows, filename: file.name.slice(0, 255) } };
  };

  const invalidateCatalog = () =>
    Promise.all([
      utils.inventory.listProductsGrouped.invalidate(),
      utils.inventory.getProductDetail.invalidate(),
      utils.inventory.listSkusForPurchaseOrder.invalidate(),
      utils.inventory.getProducts.invalidate(),
      utils.inventory.listSkus.invalidate(),
      utils.logs.getBulkImportBatches.invalidate(),
    ]);

  const apply = async (data: EditData) => {
    try {
      return await mutation.mutateAsync({ dryRun: false, items: data.items, filename: data.filename });
    } finally {
      // Success or failure, never serve a stale catalogue.
      void invalidateCatalog();
    }
  };

  const fieldLabel = (field: Field | null) => (field ? t(FIELD_KEYS[field]) : '');
  const reasonLabel = (reason: SkippedRow['reason']) => t(REASON_KEYS[reason]);
  /** One side of a change; a cost shows with that side's currency code. */
  const showValue = (change: Change, side: 'from' | 'to') => {
    if (change.field !== 'cost') return change[side];
    return formatWithCode(change[side] as number, side === 'from' ? change.fromCurrency : change.toCurrency);
  };

  const renderPreview = (preview: EditResult, data: EditData) => {
    const skippedRows: SkippedRow[] = [
      ...preview.unknown.map((row) => ({
        index: row.index,
        sku: row.sku,
        field: null,
        value: '',
        reason: 'unknown' as const,
      })),
      ...preview.skipped,
    ].sort((a, b) => a.index - b.index);
    const rowOf = (row: SkippedRow) => data.sourceRows[row.index] ?? row.index + 1;
    const skippedColumns: ErrorTableColumn<SkippedRow>[] = [
      {
        key: 'row',
        header: t('errorTable.colRow'),
        className: 'w-16',
        cellClassName: 'tabular-nums text-muted-foreground',
        cell: rowOf,
        csv: rowOf,
      },
      {
        key: 'sku',
        header: t('errorTable.colSku'),
        cellClassName: 'min-w-0 break-all font-mono',
        cell: (row) => row.sku,
        csv: (row) => row.sku,
        search: (row) => row.sku,
      },
      {
        key: 'field',
        header: t('skuBulkEdit.colField'),
        cell: (row) => fieldLabel(row.field),
        csv: (row) => fieldLabel(row.field),
        search: (row) => fieldLabel(row.field),
      },
      {
        key: 'value',
        header: t('skuBulkEdit.colValue'),
        cellClassName: 'min-w-0 break-all text-muted-foreground',
        cell: (row) => row.value,
        csv: (row) => row.value,
      },
      {
        key: 'reason',
        header: t('errorTable.colReason'),
        cellClassName: 'text-destructive',
        cell: (row) => reasonLabel(row.reason),
        csv: (row) => reasonLabel(row.reason),
        search: (row) => reasonLabel(row.reason),
      },
    ];
    const total = totalOf(preview.changed);
    const extraChanges = total - preview.sample.length;
    const thClass = 'px-3 py-1.5 text-xs font-medium text-muted-foreground';

    return (
      <>
        <ul className="space-y-0.5 text-sm tabular-nums" role="status">
          <li className="font-medium">{t('skuBulkEdit.count.change', { count: total })}</li>
          {FIELDS.filter((field) => preview.changed[field] > 0).map((field) => (
            <li key={field} className="pl-3">
              {t('skuBulkEdit.count.field', { field: fieldLabel(field), count: preview.changed[field] })}
            </li>
          ))}
          <li className="text-muted-foreground">{t('skuBulkEdit.count.match', { count: preview.unchanged })}</li>
          <li className="text-muted-foreground">{t('skuBulkEdit.count.blank', { count: preview.blank })}</li>
          <li className="text-muted-foreground">{t('skuBulkEdit.count.unknown', { count: preview.unknown.length })}</li>
          <li className="text-muted-foreground">{t('skuBulkEdit.count.skipped', { count: preview.skipped.length })}</li>
        </ul>

        {preview.blankGarmentChanges > 0 && (
          <NoteLine as="p" tone="warning" icon={AlertTriangle}>
            {t('skuBulkEdit.blanksNote', { count: preview.blankGarmentChanges })}
          </NoteLine>
        )}

        {preview.sample.length > 0 && (
          <div className="max-h-64 overflow-y-auto rounded-md border">
            <table className="w-full table-fixed text-sm" aria-label={t('skuBulkEdit.changesLabel')}>
              <thead className="sticky top-0 bg-muted">
                <tr>
                  <th scope="col" className={`${thClass} text-left`}>{t('errorTable.colSku')}</th>
                  <th scope="col" className={`${thClass} w-28 text-left`}>{t('skuBulkEdit.colField')}</th>
                  <th scope="col" className={`${thClass} text-right`}>{t('skuBulkEdit.colCurrent')}</th>
                  <th scope="col" className={`${thClass} text-right`}>{t('skuBulkEdit.colNew')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {preview.sample.map((change) => (
                  <tr key={`${change.sku}:${change.field}`}>
                    <td className="min-w-0 break-all px-3 py-1.5 font-mono text-xs">{change.sku}</td>
                    <td className="px-3 py-1.5 text-xs">{fieldLabel(change.field)}</td>
                    <td className="min-w-0 break-all px-3 py-1.5 text-right text-xs tabular-nums text-muted-foreground">
                      {change.from === null ? t('skuBulkEdit.notSet') : showValue(change, 'from')}
                    </td>
                    <td className="min-w-0 break-all px-3 py-1.5 text-right text-xs tabular-nums">
                      {showValue(change, 'to')}
                    </td>
                  </tr>
                ))}
                {extraChanges > 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-1.5 text-center text-xs text-muted-foreground">
                      {t('skuBulkEdit.andMore', { count: extraChanges })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {skippedRows.length > 0 && (
          <div className="space-y-2">
            <p id="sku-bulk-edit-skipped" className="text-sm font-medium">
              {t('skuBulkEdit.skippedHeading', { count: skippedRows.length })}
            </p>
            <p className="text-xs text-muted-foreground">{t('skuBulkEdit.skippedNote')}</p>
            <ErrorTable<SkippedRow>
              errors={skippedRows}
              columns={skippedColumns}
              rowKey={(row) => `${row.index}:${row.field ?? ''}:${row.reason}`}
              filename={`sku-bulk-edit-skipped-${new Date().toISOString().slice(0, 10)}.csv`}
              labelledById="sku-bulk-edit-skipped"
              maxHeight="200px"
            />
          </div>
        )}
      </>
    );
  };

  const renderResult = (result: EditResult) => (
    <ul className="space-y-0.5 text-sm tabular-nums" role="status">
      <li className="font-medium">{t('skuBulkEdit.done.saved', { count: totalOf(result.changed) })}</li>
      <li className="text-muted-foreground">{t('skuBulkEdit.count.match', { count: result.unchanged })}</li>
      <li className="text-muted-foreground">{t('skuBulkEdit.count.blank', { count: result.blank })}</li>
      <li className="text-muted-foreground">
        {t('skuBulkEdit.count.skipped', { count: result.unknown.length + result.skipped.length })}
      </li>
    </ul>
  );

  return (
    <CsvImportWizard<EditData, EditResult, EditResult>
      open={isOpen}
      onClose={onClose}
      title={t('skuBulkEdit.title')}
      description={t('skuBulkEdit.desc')}
      intro={
        <div className="space-y-1 text-sm text-muted-foreground">
          <p>{t('skuBulkEdit.howTo', { currency })}</p>
          <p>{t('skuBulkEdit.blankNote')}</p>
          <p>{t('skuBulkEdit.shopifyNote')}</p>
          {!canWriteCosts && <p>{t('skuBulkEdit.costAdminOnlyNote')}</p>}
        </div>
      }
      parse={parse}
      check={(data) => mutation.mutateAsync({ dryRun: true, items: data.items })}
      renderPreview={renderPreview}
      canApply={(preview) => totalOf(preview.changed) > 0}
      applyLabel={(preview) => t('skuBulkEdit.apply', { count: totalOf(preview.changed) })}
      apply={apply}
      renderResult={renderResult}
      logsHref="/logs?tab=bulk_imports"
    />
  );
};

export default SkuBulkEditModal;
